/**
 * Risk / Reward / Greeks — Phase 2 "Risk & Greeks Recalculation Engine."
 * Renders whatever `StrategyAdjustmentPage`'s debounced `recalculate()` call last
 * returned: aggregate Max Risk/Max Profit/Net Premium/Margin/Break-even/
 * Reward:Risk/Probability of Profit/Greeks, plus the per-leg breakdown
 * (delta/gamma/theta/vega/IV/premium/market value) with explicit
 * LIVE-OBSERVED / MODEL-ESTIMATE / THEORETICAL / UNAVAILABLE labeling.
 *
 * `isRefreshing` must show "Recalculating..." over the existing numbers
 * (dimmed, not replaced) rather than ever presenting a stale figure as
 * belonging to the trade currently in the Trade Builder — and a
 * `calculation_status === "FAILED"` response replaces every number with
 * an explicit "Unable to calculate" + reason, never a fallback figure.
 */
import { Fragment, useState } from "react";
import { Alert, Box, Card, CardContent, Chip, CircularProgress, Collapse, Grid, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ShieldIcon from "@mui/icons-material/Shield";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { GreeksRead, OptionLeg, PerLegGreeksRead, RecalculatedMetricsRead } from "@/types";
import { DATA_QUALITY_COLORS, DATA_QUALITY_LABELS, fmtCurrency, fmtNum } from "./constants";

/** Human-readable label for a Per-Leg Breakdown row, e.g. "CE Sell 23450"
 * (with a "(Far)" suffix for a Calendar Spread's far-dated leg, since that
 * can otherwise share type/side/strike with the near leg). Falls back to
 * the previous raw-leg_id-prefix display if no matching leg is found
 * (e.g. a stale `per_leg_greeks` row from a legs array that has since
 * changed) rather than showing nothing. */
function legLabel(leg: OptionLeg | undefined, legId: string): string {
  if (!leg) return legId.slice(0, 8);
  const side = leg.side === "BUY" ? "Buy" : "Sell";
  const far = leg.expiry_bucket === "FAR" ? " (Far)" : "";
  return `${leg.option_type} ${side} ${fmtNum(leg.strike, 0)}${far}`;
}

interface LegRow {
  detail: PerLegGreeksRead;
  leg?: OptionLeg;
}

interface LegGroup {
  key: string;
  label: string;
  rows: LegRow[];
}

/**
 * Structural Grouping (Lead Frontend Quantitative Engineer refactor,
 * requirement 2): pairs legs that share an option type and expiry into
 * their natural shape — "Call Spread" (Sell CE + Buy CE), "Put Spread"
 * (Sell PE + Buy PE) — rather than hardcoding this for named strategies
 * (Trade Builder legs can be edited into arbitrary combinations). Grouping
 * by `option_type` + `expiry_date` rather than just `option_type` keeps a
 * Calendar/Diagonal Spread's near/far legs in separate single-leg groups
 * instead of mislabeling a time spread as a vertical "Call Spread".
 */
function buildLegGroups(rows: LegRow[]): LegGroup[] {
  const groups = new Map<string, LegRow[]>();
  const order: string[] = [];
  for (const row of rows) {
    const leg = row.leg;
    const key = leg ? `${leg.option_type}|${leg.expiry_date}` : `unmatched|${row.detail.leg_id}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(row);
  }

  return order.map((key) => {
    const groupRows = groups.get(key)!;
    const leg = groupRows[0].leg;
    const optionLabel = leg?.option_type === "PE" ? "Put" : "Call";
    let label: string;
    if (!leg) {
      label = "Leg";
    } else if (groupRows.length === 1) {
      label = `${leg.side === "BUY" ? "Long" : "Short"} ${optionLabel}`;
    } else if (groupRows.length === 2 && new Set(groupRows.map((r) => r.leg?.side)).size === 2) {
      label = `${optionLabel} Spread`;
    } else {
      label = `${optionLabel} Structure (${groupRows.length} legs)`;
    }
    if (leg?.expiry_bucket === "FAR") label += " · Far Expiry";
    return { key, label, rows: groupRows };
  });
}

interface LegRoleInsight {
  icon: typeof TrendingDownIcon;
  title: string;
  body: string;
  color: "success.main" | "info.main" | "text.secondary";
}

/**
 * Leg Role & Greek Insight (requirement 2): a one-line, per-leg plain-
 * English explanation of *why* this leg is in the structure, framed around
 * its dominant Greek — short legs around the Theta income they collect,
 * long wings (a BUY leg marked as a hedge in the Trade Builder) around the
 * tail-risk capping they provide. A third, unrequested-but-necessary case
 * (a long leg that isn't a hedge — e.g. a simple long call, or a debit
 * spread's main leg) is framed around the directional exposure it provides,
 * so no leg is left without an explanation.
 */
function legRoleInsight(leg: OptionLeg | undefined, detail: PerLegGreeksRead): LegRoleInsight {
  const thetaText = `${fmtCurrency(detail.theta)}/day`;
  if (!leg) {
    return {
      icon: ShowChartIcon,
      title: "Leg role unavailable",
      body: "This row's leg is no longer in the Trade Builder, so its role here can't be described.",
      color: "text.secondary",
    };
  }
  if (leg.side === "SELL") {
    return {
      icon: TrendingDownIcon,
      title: "Short leg — Theta income",
      body: `Collects ${thetaText} in time decay while the position isn't breached — this leg's primary role is decay/income, and it carries this structure's assignment risk if the underlying moves against it.`,
      color: "success.main",
    };
  }
  if (leg.is_hedge) {
    return {
      icon: ShieldIcon,
      title: "Long wing — tail-risk protection",
      body: `Caps this side's maximum loss beyond ${fmtNum(leg.strike, 0)}, at a cost of ${thetaText} in time decay. Its role is protection, not profit — it's what makes this side's risk defined.`,
      color: "info.main",
    };
  }
  return {
    icon: ShowChartIcon,
    title: "Long leg — directional exposure",
    body: `Provides this position's directional exposure toward ${leg.option_type === "CE" ? "higher" : "lower"} underlying prices; loses ${thetaText} in time decay while price doesn't move in its favor.`,
    color: "text.secondary",
  };
}

interface Guardrail {
  key: string;
  label: string;
  flagged: boolean;
  tooltip: string;
}

/**
 * Real-Time Greek Guardrails (requirement 3): quick-read badges above the
 * trade preview, computed purely client-side from `metrics.greeks` — no new
 * backend field, since aggregate Greeks are already fetched by the
 * auto-refreshing Risk & Greeks card these badges sit inside.
 *
 * Theta/Vega ratio uses |Theta| / |Vega| (magnitudes, not signed values) —
 * the standard retail framing of "how much daily decay am I collecting per
 * unit of volatility exposure I'm carrying." Below 1.0 means a single day's
 * time decay is smaller than the P&L swing from a 1-point IV move, i.e. this
 * position's risk is currently more volatility-driven than decay-driven.
 *
 * Delta Neutrality Offset is the aggregate Delta itself (already in
 * "underlying shares equivalent" units — e.g. +37.5 behaves like being long
 * 37.5 units of the underlying at the current spot) with a plain-English
 * skew label. The magnitude bands below are a heuristic guide for framing
 * the number, not a precision claim.
 */
function computeGuardrails(greeks: GreeksRead | null | undefined): Guardrail[] {
  if (!greeks) return [];
  const guardrails: Guardrail[] = [];

  const absTheta = Math.abs(greeks.theta);
  const absVega = Math.abs(greeks.vega);
  const ratio = absVega > 0 ? absTheta / absVega : null;
  const ratioFlagged = ratio != null && ratio < 1.0;
  guardrails.push({
    key: "theta-vega",
    label: `Theta/Vega: ${ratio != null ? fmtNum(ratio, 2) : "—"}`,
    flagged: ratioFlagged,
    tooltip:
      ratio != null
        ? `|Theta| ${fmtCurrency(Math.abs(greeks.theta))}/day ÷ |Vega| ${fmtCurrency(Math.abs(greeks.vega))} = ${fmtNum(ratio, 2)}.${
            ratioFlagged
              ? " Below 1.0 — a single day's time decay is smaller than the swing from a 1-point IV move: this position is currently more volatility-driven than decay-driven."
              : " At or above 1.0 — a day's time decay outweighs the swing from a 1-point IV move."
          }`
        : "Vega is ~0 for this position — a Theta/Vega ratio isn't meaningful.",
  });

  const absDelta = Math.abs(greeks.delta);
  const deltaLabel = absDelta < 5 ? "near delta-neutral" : greeks.delta > 0 ? "bullish-skewed" : "bearish-skewed";
  guardrails.push({
    key: "delta-offset",
    label: `Delta drift: ${fmtNum(greeks.delta, 2)} (${deltaLabel})`,
    flagged: absDelta >= 30,
    tooltip: `Net Delta ${fmtNum(greeks.delta, 2)} — this position behaves like being ${
      greeks.delta >= 0 ? "long" : "short"
    } ~${fmtNum(absDelta, 0)} units of the underlying at the current spot. Larger magnitude = more directional exposure riding on this "non-directional" structure.`,
  });

  return guardrails;
}

function Metric({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  return (
    <Grid item xs={6} sm={4} md={3}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="caption" color="text.secondary" display="block">
          {label}
        </Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="h6" fontWeight={700}>
        {value}
      </Typography>
    </Grid>
  );
}

export function RecalculatedMetricsCard({
  metrics,
  legs,
  loading,
  isRefreshing,
  error,
}: {
  metrics: RecalculatedMetricsRead | null;
  /** Current Trade Builder legs, joined on `leg_id` to label the Per-Leg
   * Breakdown table's rows (see `legLabel()` above) instead of a raw ID. */
  legs: OptionLeg[];
  loading: boolean;
  isRefreshing: boolean;
  error: string | null;
}) {
  const failed = metrics?.calculation_status === "FAILED";
  const legById = new Map(legs.map((l) => [l.leg_id, l]));
  const [expandedLegs, setExpandedLegs] = useState<Set<string>>(new Set());

  function toggleExpanded(legId: string) {
    setExpandedLegs((prev) => {
      const next = new Set(prev);
      if (next.has(legId)) next.delete(legId);
      else next.add(legId);
      return next;
    });
  }

  const legGroups = metrics
    ? buildLegGroups(metrics.per_leg_greeks.map((detail) => ({ detail, leg: legById.get(detail.leg_id) })))
    : [];

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.5 }}>
            Risk / Reward / Greeks
          </Typography>
          {isRefreshing && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary" fontStyle="italic">
                Recalculating...
              </Typography>
            </Stack>
          )}
        </Stack>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !metrics ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : failed ? (
          <Alert severity="error">
            <Typography variant="body2" fontWeight={700}>
              Unable to calculate
            </Typography>
            <Typography variant="body2">{metrics?.calculation_error ?? "Unknown calculation error."}</Typography>
          </Alert>
        ) : metrics ? (
          <Box sx={{ opacity: isRefreshing ? 0.45 : 1, transition: "opacity 0.15s", pointerEvents: isRefreshing ? "none" : "auto" }}>
            {metrics.underlying_price != null && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Underlying price used: <strong>{fmtNum(metrics.underlying_price, 2)}</strong>
                </Typography>
                {metrics.underlying_price_source && (
                  <Chip size="small" label={DATA_QUALITY_LABELS[metrics.underlying_price_source]} color={DATA_QUALITY_COLORS[metrics.underlying_price_source]} />
                )}
              </Stack>
            )}

            {computeGuardrails(metrics.greeks).length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 2 }}>
                {computeGuardrails(metrics.greeks).map((guardrail) => (
                  <Tooltip key={guardrail.key} title={guardrail.tooltip}>
                    <Chip
                      size="small"
                      icon={guardrail.flagged ? <WarningAmberIcon fontSize="small" /> : undefined}
                      label={guardrail.label}
                      color={guardrail.flagged ? "warning" : "default"}
                      variant={guardrail.flagged ? "filled" : "outlined"}
                    />
                  </Tooltip>
                ))}
                <Tooltip
                  title={
                    "TekInvest doesn't yet receive live bid/ask quotes from any connected broker — only last-traded " +
                    "premium. A bid-ask-spread pre-flight check can't be computed honestly without that data, so " +
                    "this is disclosed rather than estimated. Check the live bid/ask spread in your broker's app " +
                    "before placing this order, especially in illiquid strikes."
                  }
                >
                  <Chip
                    size="small"
                    icon={<InfoOutlinedIcon fontSize="small" />}
                    label="Bid-Ask liquidity check: not available"
                    variant="outlined"
                  />
                </Tooltip>
              </Stack>
            )}

            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Metric
                label="Net Premium"
                value={fmtCurrency(metrics.net_premium)}
                tooltip="Positive = net credit received, negative = net debit paid."
              />
              <Metric label="Max Risk" value={metrics.max_risk != null ? fmtCurrency(metrics.max_risk) : "Unbounded"} />
              <Metric label="Max Profit" value={metrics.max_profit != null ? fmtCurrency(metrics.max_profit) : "Not quantifiable"} />
              <Metric
                label="Margin Required"
                value={fmtCurrency(metrics.estimated_margin)}
                tooltip="Estimated — not broker-verified. Confirm in your broker's app before executing."
              />
              <Metric
                label="Break-even"
                value={metrics.breakeven_points.length > 0 ? metrics.breakeven_points.map((p) => fmtNum(p, 0)).join(", ") : "—"}
              />
              <Metric label="Reward : Risk" value={metrics.reward_risk_ratio != null ? `${fmtNum(metrics.reward_risk_ratio, 2)} : 1` : "—"} />
              <Metric
                label="Probability of Profit"
                value={metrics.probability_of_profit != null ? `${fmtNum(metrics.probability_of_profit * 100, 1)}%` : "Model dependent"}
                tooltip="Model-based estimate from a risk-neutral price distribution — not a guarantee."
              />
            </Grid>

            <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 1 }}>
              Aggregate Greeks
            </Typography>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Metric label="Delta" value={metrics.greeks ? fmtNum(metrics.greeks.delta, 2) : "—"} />
              <Metric label="Gamma" value={metrics.greeks ? fmtNum(metrics.greeks.gamma, 4) : "—"} />
              <Metric label="Theta" value={metrics.greeks ? `${fmtCurrency(metrics.greeks.theta)}/day` : "—"} />
              <Metric label="Vega" value={metrics.greeks ? fmtCurrency(metrics.greeks.vega) : "—"} />
            </Grid>

            {metrics.per_leg_greeks.length > 0 && (
              <>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Per-Leg Breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Legs are grouped into their structural pairs (e.g. a Call Spread's Sell + Buy CE) — expand a row
                  for a plain-English note on that leg's role and the Greek behind it.
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, width: 36 }} />
                        <TableCell sx={{ fontWeight: 700 }}>Leg</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Qty</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Premium</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Premium Source</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">IV %</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>IV Source</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Market Value</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Delta</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Gamma</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Theta</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">Vega</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {legGroups.map((group) => (
                        <Fragment key={group.key}>
                          <TableRow>
                            <TableCell colSpan={12} sx={{ bgcolor: "action.hover", py: 0.5, borderBottom: "none" }}>
                              <Typography variant="caption" fontWeight={700} color="text.secondary">
                                {group.label}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          {group.rows.map(({ detail, leg }) => {
                            const expanded = expandedLegs.has(detail.leg_id);
                            const insight = legRoleInsight(leg, detail);
                            const InsightIcon = insight.icon;
                            return (
                              <Fragment key={detail.leg_id}>
                                <TableRow hover>
                                  <TableCell>
                                    <IconButton size="small" onClick={() => toggleExpanded(detail.leg_id)} aria-label="Leg role & Greek insight">
                                      {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{legLabel(leg, detail.leg_id)}</TableCell>
                                  <TableCell align="right">{detail.quantity}</TableCell>
                                  <TableCell align="right">{fmtNum(detail.premium, 2)}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={DATA_QUALITY_LABELS[detail.premium_source]} color={DATA_QUALITY_COLORS[detail.premium_source]} />
                                  </TableCell>
                                  <TableCell align="right">{fmtNum(detail.iv_pct, 2)}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={DATA_QUALITY_LABELS[detail.iv_source]} color={DATA_QUALITY_COLORS[detail.iv_source]} />
                                  </TableCell>
                                  <TableCell align="right">{fmtCurrency(detail.market_value)}</TableCell>
                                  <TableCell align="right">{fmtNum(detail.delta, 2)}</TableCell>
                                  <TableCell align="right">{fmtNum(detail.gamma, 4)}</TableCell>
                                  <TableCell align="right">{fmtNum(detail.theta, 2)}</TableCell>
                                  <TableCell align="right">{fmtNum(detail.vega, 2)}</TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={12} sx={{ py: 0, borderBottom: expanded ? undefined : "none" }}>
                                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                                      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ py: 1.25, px: 1 }}>
                                        <InsightIcon fontSize="small" sx={{ color: insight.color, mt: 0.25 }} />
                                        <Box>
                                          <Typography variant="caption" fontWeight={700} display="block" sx={{ color: insight.color }}>
                                            {insight.title}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {insight.body}
                                          </Typography>
                                        </Box>
                                      </Stack>
                                    </Collapse>
                                  </TableCell>
                                </TableRow>
                              </Fragment>
                            );
                          })}
                        </Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Alert severity="info" sx={{ mt: 2 }} icon={<InfoOutlinedIcon fontSize="inherit" />}>
              Greeks and margin are Black-Scholes / SPAN-like estimates, not values read from a broker.
            </Alert>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Add at least one leg to see recalculated metrics.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default RecalculatedMetricsCard;
